import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, setConfig, getConfig } from "@startupp-cloud/startupp-ai-sdk";
import { useAuthContext } from "../contexts/AuthContext";
import {
  tokenStorage,
  userStorage,
  sessionManager,
} from "../utils/secureStorage";
import axios from "axios";

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuthContext();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        setStatus("processing");

        // Get tokens from URL parameters
        const token = searchParams.get("token");
        const refreshToken = searchParams.get("refreshToken");

        if (!token || !refreshToken) {
          throw new Error("Missing authentication tokens in callback URL");
        }

        // Ensure SDK is properly configured with organization credentials
        console.log("[AuthCallback] Ensuring SDK configuration...");
        const currentConfig = getConfig();
        console.log("[AuthCallback] Current SDK config:", {
          hasApiBaseUrl: !!currentConfig?.apiBaseUrl,
          hasAuthBaseUrl: !!currentConfig?.authBaseUrl,
          hasOrganizationId: !!currentConfig?.organizationId,
          hasApiKey: !!currentConfig?.apiKey,
          apiKeyLength: currentConfig?.apiKey?.length || 0,
        });

        // Re-configure SDK if needed
        if (!currentConfig?.organizationId || !currentConfig?.apiKey) {
          console.log("[AuthCallback] Re-configuring SDK with organization credentials...");
          setConfig({
            apiBaseUrl: process.env.REACT_APP_API_BASE_URL || "https://api.startupp.ai",
            authBaseUrl: process.env.REACT_APP_AUTH_BASE_URL || "https://api.startupp.ai",
            organizationId: process.env.REACT_APP_DEFAULT_ORGANIZATION_ID,
            apiKey: process.env.REACT_APP_APIKEY,
            environment: process.env.NODE_ENV,
          });
        }

        // Store tokens securely using both SDK and our secure storage
        const tokenData = {
          accessToken: token,
          refreshToken: refreshToken,
        };

        console.log("[AuthCallback] Setting tokens in SDK singleton:", {
          hasAccessToken: !!tokenData.accessToken,
          hasRefreshToken: !!tokenData.refreshToken,
        });

        // Store in SDK singleton first
        auth.setTokens(tokenData);

        // Verify the SDK has the tokens
        const sdkTokens = auth.getTokens();
        console.log("[AuthCallback] SDK tokens after setTokens:", {
          hasAccessToken: !!sdkTokens.accessToken,
          hasRefreshToken: !!sdkTokens.refreshToken,
        });

        // Store in our secure storage as backup
        tokenStorage.setTokens(tokenData);

        // Force SDK to recognize the new authentication state
        // by calling getUserInfo which should use the new tokens
        console.log("[AuthCallback] Verifying tokens by getting user info...");
        
        // Debug: Check SDK configuration before making the call
        const finalConfig = getConfig();
        console.log("[AuthCallback] Final SDK config before getUserInfo:", {
          hasApiBaseUrl: !!finalConfig?.apiBaseUrl,
          hasAuthBaseUrl: !!finalConfig?.authBaseUrl,
          hasOrganizationId: !!finalConfig?.organizationId,
          hasApiKey: !!finalConfig?.apiKey,
          apiKeyLength: finalConfig?.apiKey?.length || 0,
          apiKeyStart: finalConfig?.apiKey?.substring(0, 10) || "N/A",
          apiKeyEnd: finalConfig?.apiKey?.substring(finalConfig?.apiKey?.length - 6) || "N/A",
        });

        // Debug: Check if our interceptors are set up
        console.log("[AuthCallback] Checking if axios interceptors are configured...");
        
        // Try using the SDK's getUserInfo method first
        let userInfo;
        try {
          console.log("[AuthCallback] Attempting to use SDK's getUserInfo method...");
          userInfo = await auth.getUserInfo();
          console.log("[AuthCallback] SDK getUserInfo successful:", {
            hasUserInfo: !!userInfo,
            userId: userInfo?.id || userInfo?.email,
          });
        } catch (sdkError) {
          console.log("[AuthCallback] SDK getUserInfo failed, trying direct API call:", sdkError);
          
          // Fallback to direct API call if SDK method fails
          try {
            console.log("[AuthCallback] Making direct API call with headers:", {
              url: `${process.env.REACT_APP_API_BASE_URL || "https://api.startupp.ai"}/api/v1/userInfo`,
              hasAuthHeader: !!tokenData.accessToken,
              hasOrgId: !!process.env.REACT_APP_DEFAULT_ORGANIZATION_ID,
              hasApiKey: !!process.env.REACT_APP_APIKEY,
              apiKeyLength: process.env.REACT_APP_APIKEY?.length || 0,
            });
            
            const userInfoResponse = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL || "https://api.startupp.ai"}/api/v1/userInfo`,
              {
                headers: {
                  Authorization: `Bearer ${tokenData.accessToken}`,
                  "X-Organization-Id": process.env.REACT_APP_DEFAULT_ORGANIZATION_ID,
                  "X-API-Key": process.env.REACT_APP_APIKEY,
                },
              }
            );
            
            console.log("[AuthCallback] Direct API call successful:", {
              status: userInfoResponse.status,
              hasData: !!userInfoResponse.data,
            });
            
            userInfo = userInfoResponse.data;
          } catch (directError) {
            console.error("[AuthCallback] Both SDK and direct API calls failed:", {
              sdkError: sdkError.message,
              directError: directError.message,
            });
            throw directError;
          }
        }

        if (!userInfo) {
          throw new Error("Failed to retrieve user information");
        }

        console.log("[AuthCallback] User info retrieved successfully:", {
          userId: userInfo.id || userInfo.email,
          name: userInfo.name,
        });

        // Store user info securely
        userStorage.setUser(userInfo);

        // Create session data
        sessionManager.setSession({
          userId: userInfo.id || userInfo.email,
          loginTime: Date.now(),
          loginMethod: "redirect",
        });

        // Force a complete refresh of the auth context
        console.log("[AuthCallback] Refreshing auth context...");
        await refreshUser();

        setStatus("success");

        // Redirect to the main application after a brief delay
        setTimeout(() => {
          // Check if there was a return URL in the state
          const returnUrl = window.sessionStorage.getItem("auth_return_url");
          window.sessionStorage.removeItem("auth_return_url");

          console.log("[AuthCallback] Redirecting to:", returnUrl || "/");
          navigate(returnUrl || "/", { replace: true });
        }, 1500);
      } catch (err) {
        console.error("Auth callback error:", err);
        
        // Handle specific error types
        if (err.response?.status === 403) {
          console.error("[AuthCallback] 403 Forbidden - Organization credentials issue");
          setError("Authentication failed: Organization credentials issue. Please contact support.");
        } else if (err.response?.status === 401) {
          console.error("[AuthCallback] 401 Unauthorized - Token issue");
          setError("Authentication failed: Invalid or expired token.");
        } else {
          setError(err.message || "Authentication failed");
        }
        
        setStatus("error");

        // Clear any invalid tokens from both storages
        console.log("[AuthCallback] Clearing invalid tokens...");
        auth.clearTokens();
        tokenStorage.clearTokens();
        userStorage.clearUser();
        sessionManager.clearSession();

        // Redirect to login after error delay
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate, refreshUser]);

  const renderContent = () => {
    switch (status) {
      case "processing":
        return (
          <>
            <div
              style={{
                width: "60px",
                height: "60px",
                border: "4px solid #e2e8f0",
                borderTop: "4px solid #1e293b",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 1.5rem",
              }}
            ></div>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "600",
                color: "#1e293b",
                margin: "0 0 0.5rem",
              }}
            >
              Completing Sign In
            </h2>
            <p
              style={{
                fontSize: "1rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Please wait while we securely authenticate your account...
            </p>
          </>
        );

      case "success":
        return (
          <>
            <div
              style={{
                width: "60px",
                height: "60px",
                background: "#10b981",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.5rem",
                textAlign: "center",
              }}
            >
              <svg
                style={{ width: "30px", height: "30px", color: "white" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M6 12l4 4 8-8"
                />
              </svg>
            </div>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "600",
                color: "#1e293b",
                margin: "0 0 0.5rem",
              }}
            >
              Authentication Successful
            </h2>
            <p
              style={{
                fontSize: "1rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Redirecting to your dashboard...
            </p>
          </>
        );

      case "error":
        return (
          <>
            <div
              style={{
                width: "60px",
                height: "60px",
                background: "#ef4444",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.5rem",
                textAlign: "center",
              }}
            >
              <svg
                style={{ width: "30px", height: "30px", color: "white" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M8 8l8 8M8 16l8-8"
                />
              </svg>
            </div>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "600",
                color: "#1e293b",
                margin: "0 0 0.5rem",
              }}
            >
              Authentication Failed
            </h2>
            <p
              style={{
                fontSize: "1rem",
                color: "#64748b",
                margin: "0 0 1rem",
              }}
            >
              {error || "An error occurred during authentication"}
            </p>
            <p
              style={{
                fontSize: "0.875rem",
                color: "#94a3b8",
                margin: "0",
              }}
            >
              Redirecting to login page...
            </p>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "2rem",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "3rem 2rem",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          border: "1px solid #e2e8f0",
          textAlign: "center",
          maxWidth: "400px",
          width: "100%",
        }}
      >
        {renderContent()}
      </div>

      <style jsx="true">{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default AuthCallback;
